import { SplatMeshRenderer } from "@/components/r3f/renderers/splat-mesh-renderer";
import { GaussianSplatsObject } from "@/object-cache";
import { useThree, type ThreeEvent } from "@react-three/fiber";
import { JSX, useCallback, useRef } from "react";
import { Vector2, type Vector3 } from "three";
import type { MoveCameraToCbk } from "../data-dispatch/use-move-camera-to";
import { GUIDED_NAVIGATION_PARAMS } from "../tools/guided-navigation/guided-navigation-params";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

type FlyWalkGaussianSplatsRendererProps = {
  /** The current active gaussian splats */
  gaussianSplats: GaussianSplatsObject | null;
  /** Callback issued when a point on the floor in the gaussian splats model is clicked */
  onWalkToPosition?: MoveCameraToCbk;
};

/** @returns a wrapper component to instantiate or not the gaussian splats renderer */
export function FlyWalkGaussianSplatsRenderer({
  gaussianSplats,
  onWalkToPosition,
}: FlyWalkGaussianSplatsRendererProps): JSX.Element | null {
  const {
    clippingPlanes,
    activeToolAndPicking,
    guidedNavigation,
    isGuidedNavigationActive,
  } = useToolsAcquaintance();

  const camera = useThree((s) => s.camera);

  // When a gaussian splat is clicked, see if the camera can navigate there
  // (walk and splitscreen mode only)
  const onPointClicked = useCallback(
    (point: Vector3) => {
      // Do not walk to the point is there is no walk-to callback
      // or if there is a picking tool active
      if (!onWalkToPosition || activeToolAndPicking.isPickingToolActive) {
        return;
      }
      const targetPosition = point.clone();
      if (isGuidedNavigationActive) {
        // Any point is allowed, as the destination is moved inside the trajectory tunnels
        targetPosition.y += GUIDED_NAVIGATION_PARAMS.cameraHeight;
        guidedNavigation.clampToTunnel(targetPosition);
      } else {
        // Skip points that are reasonably not on the floor
        if (point.y > camera.position.y || point.y < camera.position.y - 2) {
          return;
        }
        targetPosition.y = camera.position.y;
      }
      onWalkToPosition(targetPosition);
    },
    [
      onWalkToPosition,
      activeToolAndPicking,
      camera,
      guidedNavigation,
      isGuidedNavigationActive,
    ],
  );

  const clickPosition = useRef(new Vector2());

  const onPointerDown = useCallback((ev: ThreeEvent<PointerEvent>) => {
    clickPosition.current.set(ev.clientX, ev.clientY);
  }, []);

  const onPointerUp = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      // Checking if the user clicked/tapped in the same screen position
      if (
        clickPosition.current.distanceTo(new Vector2(ev.clientX, ev.clientY)) >
        5
      ) {
        return;
      }
      onPointClicked(ev.point);
    },
    [onPointClicked],
  );

  if (!gaussianSplats) {
    return null;
  }
  return (
    <SplatMeshRenderer
      splatMesh={gaussianSplats}
      clippingPlanes={clippingPlanes}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    />
  );
}
